// Function to draw quilt on canvas
export const drawOnCanvas = (canvasRef, quiltGrid, gridSize, logoSrc, isBasicLayout = false, tileWidthPixels, tileHeightPixels) => {
    // Enhanced validation to prevent errors
    if (!canvasRef) {
        console.warn("Canvas reference is missing");
        return;
    }
    
    // Handle both direct canvas element and React ref object
    const canvas = canvasRef.current || canvasRef;
    
    if (!canvas) {
        console.warn("Canvas element not found in reference");
        return;
    }
    
    // Check if canvas is an actual DOM element with getContext method
    if (typeof canvas.getContext !== 'function') {
        console.error("Invalid canvas element - getContext is not a function", canvas);
        return;
    }
    
    if (!quiltGrid || !Array.isArray(quiltGrid) || quiltGrid.length === 0) {
        console.warn("Quilt grid is missing or empty");
        return;
    }
    
    if (!gridSize || typeof gridSize !== 'string' || !gridSize.includes('x')) {
        console.warn("Invalid grid size format:", gridSize);
        return;
    }
    
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        console.warn("Unable to get canvas context");
        return;
    }
    
    // Parse grid dimensions correctly - first number is columns, second is rows
    // This matches how gridSize is stored and used in Firestore
    const [cols, rows] = gridSize.split('x').map(Number);
    
    if (isNaN(cols) || isNaN(rows)) {
        console.error("Invalid grid size format:", gridSize);
        return;
    }
    
    // Log the grid size and interpretation explicitly for clarity
    console.log(`Drawing canvas with grid size ${gridSize}: ${cols} columns x ${rows} rows with ${quiltGrid.length} tiles`);
    console.log(`Basic layout mode: ${isBasicLayout}`);
    
    // IMPORTANT: Extract tile dimensions from the first tile if available
    let baseTileSize;
    let useTileNativeDimensions = false;
    let uniformTileWidth, uniformTileHeight;
    
    // Check if all tiles have the same width and height
    if (quiltGrid.length > 0) {
        // Check if all tiles have width and height properties
        const allTilesHaveDimensions = quiltGrid.every(tile => 
            tile && typeof tile.width === 'number' && typeof tile.height === 'number');
        
        // Check if all tiles have the same dimensions
        if (allTilesHaveDimensions) {
            const firstTileWidth = quiltGrid[0].width;
            const firstTileHeight = quiltGrid[0].height;
            const allTilesHaveUniformDimensions = quiltGrid.every(tile => 
                tile.width === firstTileWidth && tile.height === firstTileHeight);
            
            if (allTilesHaveUniformDimensions) {
                // Use the native dimensions from the tiles
                useTileNativeDimensions = true;
                uniformTileWidth = firstTileWidth;
                uniformTileHeight = firstTileHeight;
                console.log(`Using native tile dimensions from quilt grid: ${uniformTileWidth}x${uniformTileHeight}`);
            }
        }
    }
    
    // If native dimensions are not available or inconsistent, fall back to other methods
    if (!useTileNativeDimensions) {
        // Check if we have explicit tile width/height from Firestore
        if (tileWidthPixels && !isNaN(tileWidthPixels)) {
            // Use width from Firestore directly
            baseTileSize = parseInt(tileWidthPixels);
            console.log(`Using Firestore tile width: ${baseTileSize}px`);
        } else {
            // Fall back to the calculated sizing logic if no Firestore dimensions
            console.log("No Firestore tile dimensions provided, calculating based on grid size");
            
            if (isBasicLayout) {
                // For basic layout, use larger tiles
                const totalCells = cols * rows;
                
                if (totalCells <= 16) { // 4x4
                    baseTileSize = 160;
                } else if (totalCells <= 25) { // 5x5
                    baseTileSize = 140;
                } else if (totalCells <= 36) { // 6x6
                    baseTileSize = 120;
                } else if (totalCells <= 49) { // 7x7
                    baseTileSize = 100;
                } else if (totalCells <= 64) { // 8x8
                    baseTileSize = 90;
                } else {
                    baseTileSize = 80;
                }
                
                // Apply stronger window scaling for basic layout
                const windowScaleFactor = Math.min(1, window.innerWidth / 800);
                baseTileSize = Math.round(baseTileSize * windowScaleFactor);
                
                // Ensure minimum tile size
                baseTileSize = Math.max(baseTileSize, 60);
            } else {
                // For AI layouts, use the existing smaller tiles logic
                baseTileSize = 120; // Default size for AI layouts
                const totalCells = cols * rows;
                
                if (totalCells <= 16) { // 4x4 grid
                    baseTileSize = 110;
                } else if (totalCells <= 25) { // 5x5 grid
                    baseTileSize = 90;
                } else if (totalCells <= 36) { // 6x6 grid
                    baseTileSize = 80;
                } else if (totalCells <= 49) { // 7x7
                    baseTileSize = 70;
                } else if (totalCells <= 64) { // 8x8 grid
                    baseTileSize = 60;
                } else { // Larger grids
                    baseTileSize = 50;
                }
                
                // Apply window size scaling
                const windowScaleFactor = Math.min(1, window.innerWidth / 1000);
                baseTileSize = Math.round(baseTileSize * windowScaleFactor);
            }
        }
    }
    
    // Calculate canvas dimensions
    let outerMargin;
    
    if (isBasicLayout) {
        // Use larger margins for basic layout
        outerMargin = useTileNativeDimensions 
            ? Math.round(uniformTileWidth * 0.1)  // Scale margin to native tile size
            : Math.round(baseTileSize * 0.2);
        
        // Set canvas dimensions based on tile size and grid dimensions
        if (useTileNativeDimensions) {
            // Use native dimensions for canvas size
            canvas.width = (uniformTileWidth * cols) + (outerMargin * 2);
            canvas.height = (uniformTileHeight * rows) + (outerMargin * 2) + Math.round(uniformTileHeight * 0.2);
        } else {
            canvas.width = (baseTileSize * cols) + (outerMargin * 2);
            canvas.height = (baseTileSize * rows) + (outerMargin * 2) + Math.round(baseTileSize * 0.3);
        }
    } else {
        // For AI layouts, use smaller margins
        outerMargin = Math.round(baseTileSize * 0.1);
        
        // Set canvas dimensions for AI layouts (more compact)
        canvas.width = (baseTileSize * cols) + (outerMargin * 2);
        canvas.height = (baseTileSize * rows) + (outerMargin * 2) + Math.round(baseTileSize * 0.2);
    }
    
    // Clear canvas with white background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Create a map of positions to tiles
    const tileMap = {};
    let tilesWithPosition = 0;  // Counter for tiles with valid positions
    let tilesOutOfBounds = 0;   // Count tiles with positions outside the grid
    let tilesMissingPosition = 0; // Count tiles without positions
    
    quiltGrid.forEach((tile, idx) => {
        // Add null checks to prevent errors with malformed tiles
        if (tile && tile.position && Array.isArray(tile.position) && tile.position.length === 2) {
            // Critical fix: Handle position order correctly
            // In Firestore data, position is [row, column]
            const row = tile.position[0];
            const col = tile.position[1];
            
            // Validate position is within grid bounds
            if (row >= 0 && row < rows && col >= 0 && col < cols) {
                const key = `${row},${col}`;
                tileMap[key] = tile;
                tilesWithPosition++;
                
                // Debug - Log a few positions for verification
                if ((row === 0 && col === 0) || (row === 0 && col === 1) || (row === 1 && col === 0)) {
                    console.log(`Tile mapped at position ${key}: ${tile.url ? 'Has URL' : 'No URL'}`);
                }
            } else {
                // Track out-of-bounds tiles
                tilesOutOfBounds++;
                console.warn(`Tile position out of bounds: [${row},${col}] for grid size ${rows}x${cols}`);
            }
        } else {
            // Track tiles missing positions
            tilesMissingPosition++;
            console.warn(`Tile #${idx} is missing position data:`, tile);
        }
    });
    
    // Enhanced logging with detailed breakdown
    console.log(`Mapped ${tilesWithPosition} tiles with valid positions out of ${quiltGrid.length} total tiles`);
    if (tilesOutOfBounds > 0 || tilesMissingPosition > 0) {
        console.warn(`Tile mapping issues: ${tilesOutOfBounds} out-of-bounds, ${tilesMissingPosition} missing positions`);
    }
    
    // Create image loading promises
    const imagePromises = [];
    
    // Draw grid cells and load images
    for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
            // Calculate position for this cell
            let x, y, cellWidth, cellHeight;
            
            if (useTileNativeDimensions) {
                // Use native dimensions for positioning
                x = (col * uniformTileWidth) + outerMargin;
                y = (row * uniformTileHeight) + outerMargin;
                cellWidth = uniformTileWidth;
                cellHeight = uniformTileHeight;
            } else {
                // Use calculated dimensions
                x = (col * baseTileSize) + outerMargin;
                y = (row * baseTileSize) + outerMargin;
                cellWidth = baseTileSize;
                cellHeight = baseTileSize;
            }
            
            // Fill with white background and add gray border for visibility
            ctx.fillStyle = 'white';
            ctx.fillRect(x, y, cellWidth, cellHeight);
            ctx.strokeStyle = '#ddd';
            ctx.lineWidth = 1;
            ctx.strokeRect(x, y, cellWidth, cellHeight);
            
            // Load and draw the image for this cell if available
            const key = `${row},${col}`;
            const tile = tileMap[key];
            
            if (tile && tile.url) {
                const imgPromise = new Promise((resolve, reject) => {
                    const img = new Image();
                    img.crossOrigin = "Anonymous";
                    
                    img.onload = () => {
                        // Draw the image to fit the cell
                        ctx.drawImage(img, x, y, cellWidth, cellHeight);
                        resolve();
                    };
                    
                    img.onerror = () => {
                        console.error(`Failed to load image: ${tile.url}`);
                        // Draw a placeholder for missing images
                        ctx.fillStyle = '#f0f0f0';
                        ctx.fillRect(x, y, cellWidth, cellHeight);
                        
                        // Add a cross to indicate missing image
                        ctx.beginPath();
                        ctx.moveTo(x, y);
                        ctx.lineTo(x + cellWidth, y + cellHeight);
                        ctx.moveTo(x + cellWidth, y);
                        ctx.lineTo(x, y + cellHeight);
                        ctx.strokeStyle = '#cccccc';
                        ctx.lineWidth = 2;
                        ctx.stroke();
                        
                        // Add text to indicate missing image
                        const fontSize = Math.max(8, Math.min(12, cellWidth / 10));
                        ctx.font = `${fontSize}px Arial`;
                        ctx.fillStyle = '#999';
                        ctx.textAlign = 'center';
                        ctx.fillText('Image not found', x + cellWidth/2, y + cellHeight/2);
                        
                        resolve();
                    };
                    
                    img.src = tile.url;
                });
                
                imagePromises.push(imgPromise);
            } else {
                // Draw empty cell placeholder
                ctx.fillStyle = '#f5f5f5';
                ctx.fillRect(x, y, cellWidth, cellHeight);
                
                // Add tile position text for debugging - only in small text
                const fontSize = Math.max(8, Math.min(10, cellWidth / 12));
                ctx.font = `${fontSize}px Arial`;
                ctx.fillStyle = '#aaa';
                ctx.textAlign = 'center';
                ctx.fillText(`${row},${col}`, x + cellWidth/2, y + cellHeight/2);
            }
        }
    }
    
    // Add logo and attribution text
    if (logoSrc) {
        const logoPromise = new Promise((resolve, reject) => {
            const logo = new Image();
            logo.onload = () => {
                // Adjust logo size based on canvas size
                const logoBaseSize = useTileNativeDimensions ? uniformTileWidth : baseTileSize;
                const logoSizeBase = isBasicLayout ? Math.round(logoBaseSize * 0.15) : Math.round(logoBaseSize * 0.25);
                const logoSize = Math.max(16, Math.min(42, logoSizeBase));
                
                const logoX = canvas.width - logoSize - 10;
                const logoY = canvas.height - logoSize - 10;
                
                // Draw logo
                ctx.drawImage(logo, logoX, logoY, logoSize, logoSize);
                
                // Add "Generated with NOSTAI" text
                const fontSize = Math.max(10, Math.min(16, logoSize * 0.6)); 
                ctx.font = `bold ${fontSize}px Arial`;
                ctx.fillStyle = 'black';
                ctx.textAlign = 'right';
                ctx.fillText('Generated with NOSTAI', logoX - 5, logoY + (logoSize / 2) + 4);
                
                resolve();
            };
            
            logo.onerror = () => {
                console.error("Failed to load logo");
                resolve(); // Resolve anyway to not block other operations
            };
            
            logo.src = logoSrc;
        });
        
        imagePromises.push(logoPromise);
    }
    
    // Handle any errors loading images
    Promise.all(imagePromises).catch(error => {
        console.error("Error loading images:", error);
    });
    
    // Log canvas dimensions for debugging with explicit cols/rows
    if (useTileNativeDimensions) {
        console.log(`Canvas dimensions: ${canvas.width}x${canvas.height}, tile size: ${uniformTileWidth}x${uniformTileHeight}, columns: ${cols}, rows: ${rows}`);
    } else {
        console.log(`Canvas dimensions: ${canvas.width}x${canvas.height}, tile size: ${baseTileSize}, columns: ${cols}, rows: ${rows}`);
    }
};

// Function to draw error message on canvas
export const drawErrorMessage = (canvas, message) => {
    if (!canvas) return;
    
    try {
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#fff0f0';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.font = '16px Arial';
        ctx.fillStyle = '#d32f2f';
        ctx.textAlign = 'center';
        ctx.fillText(message, canvas.width / 2, canvas.height / 2);
        
        // Draw border
        ctx.strokeStyle = '#d32f2f';
        ctx.lineWidth = 2;
        ctx.strokeRect(5, 5, canvas.width - 10, canvas.height - 10);
    } catch (error) {
        console.error("Error drawing error message:", error);
    }
};
